import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { ThemePalette } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { Constants } from 'src/app/app.constants';
import { AnalyticsAbstract } from 'src/app/services/analytics/analytics.abstract';
import { TextAreaDialogComponent } from 'src/app/shared/components/text-area-dialog/text-area-dialog.component';
import { TwoOptionAlertComponent } from 'src/app/shared/components/two-option-alert/two-option-alert.component';
import { SingleInputDialogComponent } from 'src/app/shared/components/single-input-dialog/single-input-dialog.component';
import { GiveRewardDialogComponent } from '../give-reward-dialog/give-reward-dialog.component';
import {
  RewardService,
  MerchantService,
  CardService,
  AuthService,
  LocationService,
} from '../../../services/index';
import {
  StampCard,
  UserCard,
  UserReward,
  User,
  Redemption,
  StampEvent,
  Location,
  Customer,
  CardType,
} from '../../../models/index';
import { GiveStampsDialogComponent } from '../give-stamps-dialog/give-stamps-dialog.component';
import { AdjustPointsDialogComponent } from '../adjust-points-dialog/adjust-points-dialog.component';
import { SingleSelectDialogComponent } from 'src/app/shared/components/single-select-dialog/single-select-dialog.component';
import { UserVoucher } from 'src/app/models/user_voucher';
import { RedeemVoucherDialogComponent } from '../../gift-vouchers/redeem-voucher-dialog/redeem-voucher-dialog.component';
import { VoucherService } from 'src/app/services/voucher.service';

@Component({
  selector: 'customer-detail',
  templateUrl: './customer-detail.component.html',
  styleUrls: ['./customer-detail.component.css'],
})
export class CustomerDetailComponent {
  chartType = 'line';
  title = '';
  subtitle: string = '';
  @ViewChild('stampsTable') stampsTable: any;
  @ViewChild('pointsTable') pointsTable: any;
  @ViewChild('cardsTable') cardsTable: any;
  @ViewChild('rewardsTable') rewardsTable: any;
  @ViewChild('redemptionsTable') redemptionsTable: any;
  @ViewChild('vouchersTable') vouchersTable: any;

  customer: Customer = {};

  cardIndex = 0;
  cardSize = 30;
  cardLength = 0;

  stampIndex = 0;
  stampSize = 30;
  stampLength = 0;

  pointsIndex = 0;
  pointsSize = 30;
  pointsLength = 0;

  redemptionIndex = 0;
  redemptionSize = 30;
  redemptionLength = 0;

  rewardIndex = 0;
  rewardSize = 30;
  rewardLength = 0;

  vouchersPageIndex = 0;
  vouchersPageSize = 20;
  vouchersLength = 0;

  userCards: UserCard[] = [];
  userRewards: UserReward[] = [];
  stampEvents: StampEvent[] = [];
  pointsEvents: StampEvent[] = [];
  redemptions: Redemption[] = [];
  userVouchers: UserVoucher[] = [];

  customerLoading = false;
  locationsLoading = false;
  nextLoading: boolean = false;

  saving = false;
  paging = false;
  error = false;

  cardMenuOptions = [
    { title: 'Give Stamps', pointsTitle: 'Give Points', icon: 'approval' },
    {
      title: 'Adjust Points',
      pointsTitle: 'Adjust Points',
      icon: 'tune',
      openEndedOnly: true,
    },
    // { title: 'Deactivate', icon: 'cancel' },
  ];

  rewardMenuOptions = [
    { title: 'Redeem Reward', icon: 'redeem' },
    { title: 'Expire Reward', icon: 'event_busy' },
    { title: 'Update Hidden Text', icon: 'edit_note' },
  ];

  color: ThemePalette = 'primary';
  mode: ProgressSpinnerMode = 'determinate';

  tabs = [
    'overview',
    'stamps',
    'points',
    'redemptions',
    'cards',
    'rewards',
    'vouchers',
  ];
  selectedTab = 'overview';
  selectedIndex = 0;
  hasStampCards: boolean | null = null;
  hasPointsCards: boolean | null = null;
  hasVouchers: boolean | null = null;

  planLimitsLoading = false;
  canManageCustomer = false;
  vouchersLoading = false;
  scrollTimeout: any;
  suppressPaging: boolean = false;

  vouchersRange = '';
  cardsRange = '';
  rewardsRange = '';
  stampsRange = '';
  pointsRange = '';
  redemptionsRange = '';

  days = '30';

  pushNotificationsEnabled = false;
  emailNotificationsEnabled = false;

  merchantActive = false;

  @ViewChild('pushToggle') pushToggle?: ElementRef;
  @ViewChild('emailToggle') emailToggle?: ElementRef;

  constructor(
    private cardService: CardService,
    private rewardService: RewardService,
    public constants: Constants,
    private route: ActivatedRoute,
    private authService: AuthService,
    private merchantService: MerchantService,
    private router: Router,
    public dialog: MatDialog,
    private mixpanel: AnalyticsAbstract,
    private locationService: LocationService,
    private voucherService: VoucherService,
  ) {
    this.route.queryParams.subscribe((params) => {
      const tab = params['tab'];
      if (tab) {
        this.selectTab(tab);
      }
    });

    this.route.params.subscribe(
      (params) => (this.customer.nanoid = params['id']),
    );

    if (this.customer.nanoid) {
      this.getCustomer();
    }

    this.getPlanLimits();
  }

  ngOnInit() {
    this.merchantActive = this.authService.merchantObj?.active ?? true;
    this.subtitle = this.authService.getNestedUserProperty('merchant', 'name');
    this.getCustomerTabAvailability();
  }

  get visibleTabs(): string[] {
    return this.tabs.filter((tab) => this.isTabVisible(tab));
  }

  private isTabVisible(tab: string): boolean {
    if (tab === 'stamps') {
      return this.hasStampCards !== false;
    }

    if (tab === 'points') {
      return this.hasPointsCards !== false;
    }

    if (tab === 'vouchers') {
      return this.hasVouchers !== false;
    }

    return true;
  }

  private selectTab(tab: string): void {
    this.selectedTab =
      this.tabs.includes(tab) && this.isTabVisible(tab) ? tab : 'overview';
    this.syncSelectedIndex();
  }

  private syncSelectedIndex(): void {
    if (!this.isTabVisible(this.selectedTab)) {
      this.selectedTab = 'overview';
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { tab: this.selectedTab },
        queryParamsHandling: 'merge',
        skipLocationChange: false,
      });
    }

    this.selectedIndex = this.visibleTabs.findIndex(
      (tab) => tab === this.selectedTab,
    );

    if (this.selectedIndex < 0) {
      this.selectedIndex = 0;
    }
  }

  getCustomerTabAvailability(): void {
    this.cardService.getCards(1000, 0).subscribe({
      next: (res: any) => {
        const cards: StampCard[] = res?.docs ?? [];
        this.hasStampCards = cards.some(
          (card) => card.cardType === CardType.stamp,
        );
        this.hasPointsCards = cards.some(
          (card) =>
            card.cardType === CardType.point ||
            card.cardType === CardType.openEndedPoint,
        );
        this.syncSelectedIndex();
      },
      error: () => {
        this.hasStampCards = true;
        this.hasPointsCards = true;
      },
    });

    this.voucherService.getVouchers(1, 0, true).subscribe({
      next: (res: any) => {
        this.hasVouchers = (res?.totalDocs ?? res?.docs?.length ?? 0) > 0;
        this.syncSelectedIndex();
      },
      error: () => {
        this.hasVouchers = true;
      },
    });
  }

  get tooltip(): string {
    if (this.loading) {
      return '';
    }
    if (!this.authService.merchantObj?.active) {
      return this.constants.strings.inactive;
    } else if (!this.canManageCustomer) {
      return this.constants.strings.upgradePlanNotice;
    } else {
      return '';
    }
  }

  getPlanLimits(): void {
    this.planLimitsLoading = true;
    this.merchantService
      .getPlanLimits()
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.canManageCustomer = res.manage ?? false;
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.planLimitsLoading = false));
  }

  isUser(user?: string | User): user is User {
    return (
      typeof user !== 'string' &&
      user !== null &&
      typeof user?.name === 'string'
    );
  }

  get customerEmail(): string {
    if (this.isUser(this.customer.user)) {
      return this.customer.user.email ?? 'Hidden';
    }

    return this.customer.email ?? '';
  }

  voucherPageEvent(event: any) {
    if (!this.suppressPaging) {
      this.vouchersPageIndex = event.offset;
      this.addQueryParams({ p: this.vouchersPageIndex });
      this.getUserVouchers(true);
    }
  }

  getUserVouchers(paging: boolean): void {
    this.vouchersLoading = true;

    this.merchantService
      .getCustomerVouchers(
        this.vouchersPageSize,
        this.vouchersPageIndex,
        this.customer._id,
      )
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.vouchersLength = res.totalDocs;
            this.userVouchers = res.docs;

            this.vouchersRange = this.constants.pageCounter(
              this.vouchersPageIndex,
              this.vouchersPageSize,
              this.vouchersLength,
              this.userVouchers?.length,
            );
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.vouchersLoading = false));
  }

  addQueryParams(params: any) {
    this.route.queryParams.subscribe((queryParams) => {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: params,
        queryParamsHandling: 'merge',
        skipLocationChange: false,
      });
    });
  }

  tabChanged(index: any): void {
    let tab = this.visibleTabs[index];

    if (!tab) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        tab: tab,
      },
      queryParamsHandling: 'merge',
      skipLocationChange: false,
    });

    this.selectedTab = tab;

    this.loadData();
  }

  stampPageEvent(event: any) {
    if (!this.suppressPaging) {
      this.stampIndex = event.offset;
      this.getStampEvents(true);
    }
  }

  pointsPageEvent(event: any) {
    if (!this.suppressPaging) {
      this.pointsIndex = event.offset;
      this.getStampEvents(true, true);
    }
  }

  rewardPageEvent(event: any) {
    if (!this.suppressPaging) {
      this.redemptionIndex = event.offset;
      this.getCustomerRewards(true);
    }
  }

  cardPageEvent(event: any) {
    if (!this.suppressPaging) {
      this.cardIndex = event.offset;
      this.getCustomerCards(true);
    }
  }

  redemptionPageEvent(event: any) {
    if (!this.suppressPaging) {
      this.redemptionIndex = event.offset;
      this.getRedemptions(true);
    }
  }

  rowTappedNGX({ row, type }: any): void {
    if (type === 'click') {
      const dialogRef = this.dialog.open(RedeemVoucherDialogComponent, {
        data: { voucherDetails: row },
        width: '700px',
        autoFocus: false,
        disableClose: true,
        scrollStrategy: new NoopScrollStrategy(),
        panelClass: 'custom-dialog',
      });

      dialogRef.afterClosed().subscribe((result: boolean) => {
        if (result) {
          this.loadData();
        }
      });
    }
  }

  loadData(): void {
    if (this.selectedTab === 'stamps') {
      this.getStampEvents(false);
    }
    if (this.selectedTab === 'points') {
      this.getStampEvents(false, true);
    }
    if (this.selectedTab === 'cards') {
      this.getCustomerCards(false);
    }
    if (this.selectedTab === 'rewards') {
      this.getCustomerRewards(false);
    }
    if (this.selectedTab === 'redemptions') {
      this.getRedemptions(false);
    }
    if (this.selectedTab === 'vouchers') {
      this.getUserVouchers(false);
    }
  }

  getCustomer(): void {
    this.customerLoading = true;
    this.merchantService
      .getCustomer(this.customer.nanoid!)
      .subscribe({
        next: (res: Customer) => {
          if (res) {
            this.customer = res;

            this.title = this.getCustomerName(res);

            this.loadData();

            this.setNotificationEnabledSettings();
          }
        },
        error: (res: HttpErrorResponse) => {
          this.router
            .navigate(['/' + Constants.routes.dashboard], {})
            .then(() => {
              window.location.reload();
            });
        },
      })
      .add(() => (this.customerLoading = false));
  }

  getCustomerName(res: Customer): string {
    if (typeof res.user === 'string') {
      return res.user ?? '';
    } else {
      return res.user?.name ?? res?.name ?? '';
    }
  }

  getStampEvents(paging: boolean, points = false): void {
    paging ? (this.paging = true) : (this.nextLoading = true);
    this.cardService
      .getStampEvents(
        points ? this.pointsSize : this.stampSize,
        points ? this.pointsIndex : this.stampIndex,
        undefined,
        this.customer._id,
        points,
      )
      .subscribe({
        next: (res: any) => {
          if (res) {
            if (points) {
              this.pointsLength = res.totalDocs;
              this.pointsEvents = res.docs;

              this.pointsRange = this.constants.pageCounter(
                this.pointsIndex,
                this.pointsSize,
                this.pointsLength,
                this.pointsEvents?.length,
              );
            } else {
              this.stampLength = res.totalDocs;
              this.stampEvents = res.docs;

              this.stampsRange = this.constants.pageCounter(
                this.stampIndex,
                this.stampSize,
                this.stampLength,
                this.stampEvents?.length,
              );
            }
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (paging ? (this.paging = false) : (this.nextLoading = false)));
  }

  getRedemptions(paging: boolean): void {
    paging ? (this.paging = true) : (this.nextLoading = true);
    this.rewardService
      .getRedemptions(
        this.redemptionSize,
        this.redemptionIndex,
        undefined,
        this.customer._id,
      )
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.redemptionLength = res.totalDocs;
            this.redemptions = res.docs;

            this.redemptionsRange = this.constants.pageCounter(
              this.redemptionIndex,
              this.redemptionSize,
              this.redemptionLength,
              this.redemptions?.length,
            );
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (paging ? (this.paging = false) : (this.nextLoading = false)));
  }

  getCustomerCards(paging: boolean): void {
    paging ? (this.paging = true) : (this.nextLoading = true);
    this.merchantService
      .getCustomerCards(this.cardSize, this.cardIndex, this.customer._id!)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.cardLength = res.totalDocs;
            this.userCards = res.docs;

            this.cardsRange = this.constants.pageCounter(
              this.cardIndex,
              this.cardSize,
              this.cardLength,
              this.userCards?.length,
            );
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (paging ? (this.paging = false) : (this.nextLoading = false)));
  }

  getCustomerRewards(paging: boolean): void {
    paging ? (this.paging = true) : (this.nextLoading = true);
    this.merchantService
      .getCustomerRewards(this.rewardSize, this.rewardIndex, this.customer._id!)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.rewardLength = res.totalDocs;
            this.userRewards = res.docs;

            this.rewardsRange = this.constants.pageCounter(
              this.rewardIndex,
              this.rewardSize,
              this.rewardLength,
              this.userRewards?.length,
            );
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (paging ? (this.paging = false) : (this.nextLoading = false)));
  }

  onClick(event: any, item: any) {
    if (!this.canManageCustomer) {
      return;
    }

    if (event === 'Give Stamps') {
      this.openGiveStampDialog(item);
    } else if (event === 'Adjust Points') {
      this.openAdjustPointsDialog(item);
    } else if (event === 'Redeem Reward') {
      if (this.userRewards.find((el) => el._id === item)?.hasRedeemed) {
        this.constants.snack('Reward has already been redeemed');
        return;
      }

      if (this.userRewards.find((el) => el._id === item)?.hasExpired) {
        this.constants.snack('Reward has already expired');
        return;
      }

      const dialogRef = this.dialog.open(TwoOptionAlertComponent, {
        data: {
          title: 'Confirm',
          body: 'Do you want to mark this reward as redeemed?',
          buttonOne: 'Cancel',
          buttonTwo: 'Yes',
        },
        scrollStrategy: new NoopScrollStrategy(),
        autoFocus: false,
        disableClose: true,
      });
      dialogRef.afterClosed().subscribe((option: number) => {
        if (option == 1) {
          this.getLocations(item);
        }
      });
    } else if (event === 'Expire Reward') {
      if (this.userRewards.find((el) => el._id === item)?.hasExpired) {
        this.constants.snack('Reward has already expired');
        return;
      }

      if (this.userRewards.find((el) => el._id === item)?.hasRedeemed) {
        this.constants.snack('Reward has already been redeemed');
        return;
      }

      const dialogRef = this.dialog.open(TwoOptionAlertComponent, {
        data: {
          title: 'Confirm',
          body: 'Do you want to mark this reward as expired?',
          buttonOne: 'Cancel',
          buttonTwo: 'Yes',
        },
        scrollStrategy: new NoopScrollStrategy(),
        autoFocus: false,
        disableClose: true,
      });
      dialogRef.afterClosed().subscribe((option: number) => {
        if (option == 1) {
          this.expireReward(item);
        }
      });
    } else if (event === 'Update Hidden Text') {
      const reward = this.userRewards.find((el) => el._id === item);
      const dialogRef = this.dialog.open(SingleInputDialogComponent, {
        data: {
          title: 'Update Hidden Text',
          label: 'Hidden Text',
          value: reward?.rewardObj?.hiddenText ?? '',
          maxLength: 500,
        },
        scrollStrategy: new NoopScrollStrategy(),
        width: '600px',
        autoFocus: false,
        disableClose: true,
      });
      dialogRef.afterClosed().subscribe((result?: any) => {
        if (result?.changed) {
          this.updateRewardHiddenText(item, result.value);
        }
      });
    }
  }

  processExpireReward(reward: UserReward) {
    const dialogRef = this.dialog.open(TwoOptionAlertComponent, {
      data: {
        title: 'Confirm',
        body: 'Do you want to mark this reward as expired?',
        buttonOne: 'Cancel',
        buttonTwo: 'Yes',
      },
      scrollStrategy: new NoopScrollStrategy(),
      autoFocus: false,
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((option: number) => {
      if (option == 1) {
        this.expireReward(reward._id!);
      }
    });
  }

  openGiveStampDialog(userCard: any): void {
    this.mixpanel.track(Constants.analytics_keys.openStampPicker, {
      Customer: this.getCustomerName(this.customer),
    });

    const dialogRef = this.dialog.open(GiveStampsDialogComponent, {
      data: {
        customer: this.customer,
        userCard: userCard,
      },
      scrollStrategy: new NoopScrollStrategy(),
      width: '600px',
      autoFocus: false,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.success) {
        // Refresh the page to show updated data
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    });
  }

  openAdjustPointsDialog(userCard?: any): void {
    this.mixpanel.track(Constants.analytics_keys.openStampPicker, {
      Customer: this.getCustomerName(this.customer),
    });

    const dialogData: any = {
      customer: this.customer,
    };

    if (userCard) {
      dialogData.userCard = userCard;
    }

    const dialogRef = this.dialog.open(AdjustPointsDialogComponent, {
      data: dialogData,
      scrollStrategy: new NoopScrollStrategy(),
      width: '500px',
      autoFocus: false,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.success) {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    });
  }

  rewardsToGive(stamps: number, userCard: any): number {
    const totalStamps = (userCard.numberOfStamps ?? 0) + stamps;

    let rewardsCount = 0;

    const stampCard =
      userCard.card && typeof userCard.card !== 'string'
        ? Array.isArray(userCard.card)
          ? userCard.card[0]
          : userCard.card
        : null;
    const rewards =
      (stampCard && typeof stampCard !== 'string' ? stampCard.rewards : null) ??
      [];

    if (rewards && rewards.length > 0) {
      rewards.forEach((reward: { stamp: number }) => {
        if (
          userCard.numberOfStamps < reward.stamp &&
          totalStamps >= reward.stamp
        ) {
          rewardsCount += 1;
        }
      });
    }

    return rewardsCount;
  }

  rewardsToGiveFromPoints(points: number, userCard: any): number {
    const totalPoints = (userCard?.card?.points?.totalPoints ?? 0) + points;

    let rewardsCount = 0;

    // Get the list of rewards, checking for the 'points' field
    const stampCard =
      userCard.card && typeof userCard.card !== 'string'
        ? Array.isArray(userCard.card)
          ? userCard.card[0]
          : userCard.card
        : null;
    const rewards =
      (stampCard && typeof stampCard !== 'string' ? stampCard.rewards : null) ??
      [];

    if (rewards && rewards.length > 0) {
      rewards.forEach((reward: { points: any }) => {
        if (
          userCard?.points?.totalPoints < (reward.points ?? 0) &&
          totalPoints >= (reward.points ?? 0)
        ) {
          rewardsCount += 1;
        }
      });
    }

    return rewardsCount;
  }

  giveReward(): void {
    const customerName = this.getCustomerName(this.customer);

    this.mixpanel.track(Constants.analytics_keys.openGiveReward, {
      Customer: customerName,
    });

    this.dialog.open(GiveRewardDialogComponent, {
      data: {
        customer: this.customer,
      },
      scrollStrategy: new NoopScrollStrategy(),
      width: '450px',
      autoFocus: false,
      panelClass: 'custom-dialog-panel',
      disableClose: true,
    });
  }

  openGiveStampsFromCustomerPage(): void {
    const dialogRef = this.dialog.open(GiveStampsDialogComponent, {
      data: {
        customer: this.customer,
      },
      scrollStrategy: new NoopScrollStrategy(),
      width: '600px',
      autoFocus: false,
      panelClass: 'custom-dialog-panel',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.success) {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    });
  }

  giveStamp(
    cardId: string,
    quantity: number,
    isPoints: boolean,
    location?: string,
  ): void {
    let analyticsPayload = {
      Customer: this.getCustomerName(this.customer),
      Card: cardId,
      Points: 0,
      Stamps: 0,
    };

    if (isPoints) {
      analyticsPayload.Points = quantity;
    } else {
      analyticsPayload.Stamps = quantity;
    }

    this.mixpanel.track(
      isPoints
        ? Constants.analytics_keys.givePoints
        : Constants.analytics_keys.giveStamps,
      analyticsPayload,
    );

    this.saving = true;
    this.merchantService
      .giveStamp(
        typeof this.customer.user === 'string'
          ? this.customer.user!
          : this.customer.user?._id!,
        cardId,
        quantity,
        location,
      )
      .subscribe({
        next: (res: Customer) => {
          if (res) {
            this.constants.snack(
              isPoints
                ? 'Point(s) awarded to customer'
                : 'Stamp(s) awarded to customer',
            );

            setTimeout(() => {
              window.location.reload();
            }, 500);
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.saving = false));
  }

  getLocations(redeemId: string): void {
    this.locationsLoading = true;

    this.locationService
      .getLocations(1000, 0)
      .subscribe({
        next: (res: any) => {
          if (res) {
            const locations = res.docs.filter(
              (el: Location) => el.active === true && el.approved === true,
            );

            this.selectLocationForRewardRedemption(redeemId, locations);
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => {
        this.locationsLoading = false;
      });
  }

  selectLocationForRewardRedemption(
    redeemId: string,
    locations: string[] = [],
  ): void {
    if (locations.length > 1) {
      const dialogRef = this.dialog.open(SingleSelectDialogComponent, {
        data: {
          title: 'Select a location',
          label: 'Location',
          property: 'name',
          options: locations,
          infoText:
            'Select a location to attribute this redemption to for reporting purposes',
        },
        scrollStrategy: new NoopScrollStrategy(),
        width: '600px',
        autoFocus: false,
        disableClose: true,
      });

      dialogRef.afterClosed().subscribe((data) => {
        if (data) {
          this.redeemReward(redeemId, data._id);
        }
      });
    } else {
      this.redeemReward(redeemId);
    }
  }

  redeemReward(redeemId: string, location?: string): void {
    this.saving = true;
    this.merchantService
      .redeemReward(this.customer._id!, redeemId, location)
      .subscribe({
        next: (res: Customer) => {
          if (res) {
            this.constants.snack('Reward redeemed');

            setTimeout(() => {
              window.location.reload();
            }, 500);
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.saving = false));
  }

  expireReward(redeemId: string): void {
    this.saving = true;
    this.merchantService
      .expireReward(this.customer._id!, redeemId)
      .subscribe({
        next: (res: Customer) => {
          if (res) {
            this.constants.snack('Reward expired');

            setTimeout(() => {
              window.location.reload();
            }, 500);
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.saving = false));
  }

  updateRewardHiddenText(rewardId: string, hiddenText: string): void {
    this.saving = true;
    this.merchantService
      .updateRewardHiddenText(this.customer._id!, rewardId, hiddenText)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.constants.snack('Hidden text updated');
            setTimeout(() => {
              window.location.reload();
            }, 500);
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.saving = false));
  }

  get loading(): boolean {
    return this.customerLoading || this.planLimitsLoading;
  }

  handleScroll() {
    this.suppressPaging = true;

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = setTimeout(() => {
      this.suppressPaging = false;
    }, 100);
  }

  getPreferences(type: string): string {
    let list: string[] = [];

    if (typeof this.customer.user !== 'string') {
      if (this.customer.user?.preferences[type]) {
        for (var pref of this.customer.user?.preferences[type]) {
          list.push(pref);
        }
      }
    }

    return list.join(', ');
  }

  get userId(): string {
    if (typeof this.customer.user === 'string') {
      return this.customer.user ?? '';
    } else {
      return this.customer.user!._id ?? '';
    }
  }

  editNotes(): void {
    const dialogRef = this.dialog.open(TextAreaDialogComponent, {
      data: {
        title: 'Customer Notes',
        label: 'Notes',
        content: this.customer?.notes,
      },
      scrollStrategy: new NoopScrollStrategy(),
      width: '600px',
      autoFocus: false,
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((content?: string) => {
      if (content) {
        this.updateCustomer(content);
      }
    });
  }

  editCustomerName(): void {
    if (this.isUser(this.customer.user)) {
      return; // Only allow editing for manually created customers
    }

    const dialogRef = this.dialog.open(SingleInputDialogComponent, {
      data: {
        title: 'Edit Customer Name',
        label: 'Name',
        value: this.customer.name ?? '',
        maxLength: 100,
      },
      scrollStrategy: new NoopScrollStrategy(),
      width: '600px',
      autoFocus: false,
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((result?: any) => {
      if (result?.changed) {
        this.updateCustomerField('name', result.value);
      }
    });
  }

  editCustomerEmail(): void {
    if (this.isUser(this.customer.user)) {
      return; // Only allow editing for manually created customers
    }

    const dialogRef = this.dialog.open(SingleInputDialogComponent, {
      data: {
        title: 'Edit Customer Email',
        label: 'Email',
        value: this.customer.email ?? '',
        maxLength: 100,
        inputType: 'email',
      },
      scrollStrategy: new NoopScrollStrategy(),
      width: '600px',
      autoFocus: false,
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((result?: any) => {
      if (result?.changed) {
        this.updateCustomerField('email', result.value);
      }
    });
  }

  editCustomerDob(): void {
    if (this.isUser(this.customer.user)) {
      return; // Only allow editing for manually created customers
    }

    const dialogRef = this.dialog.open(SingleInputDialogComponent, {
      data: {
        title: 'Edit Customer DOB',
        label: 'Date of Birth',
        value: this.customer.dob ?? '',
        maxLength: 10,
        inputType: 'date',
      },
      scrollStrategy: new NoopScrollStrategy(),
      width: '600px',
      autoFocus: false,
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((result?: any) => {
      if (result?.changed) {
        this.updateCustomerField('dob', result.value);
      }
    });
  }

  updateCustomer(notes: string): void {
    this.saving = true;
    this.merchantService
      .updateCustomer(this.customer._id!, 'notes', notes)
      .subscribe({
        next: (res: Customer) => {
          if (res) {
            this.constants.snack('Customer Updated');
            this.customer.notes = notes;
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.saving = false));
  }

  updateCustomerField(field: string, value: string): void {
    this.saving = true;
    this.merchantService
      .updateCustomer(this.customer._id!, field, value)
      .subscribe({
        next: (res: Customer) => {
          if (res) {
            this.constants.snack('Customer Updated');
            if (field === 'name') {
              this.customer.name = value;
              this.title = value;
            } else if (field === 'email') {
              this.customer.email = value;
            } else if (field === 'dob') {
              this.customer.dob = value;
            }
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.saving = false));
  }

  valueObtainedPercent(card: any): number {
    const stampCard =
      card?.card && typeof card.card !== 'string'
        ? (card.card?.[0] ?? card.card)
        : card.card;

    if (stampCard?.cardType == CardType.point) {
      const totalPoints = stampCard?.points?.totalPoints ?? 0;
      const currentPoints = card?.numberOfPoints ?? 0;
      return totalPoints ? (currentPoints / totalPoints) * 100 : 0;
    } else if (stampCard?.cardType == CardType.openEndedPoint) {
      const maxPoints = stampCard?.points?.maxPoints ?? 0;
      const currentPoints = card?.numberOfPoints ?? 0;

      if (maxPoints > 0) {
        return (currentPoints / maxPoints) * 100;
      } else {
        return 0;
      }
    } else {
      const totalStamps = stampCard?.numberOfStamps ?? 0;
      const currentStamps = card?.numberOfStamps ?? 0;
      return totalStamps ? (currentStamps / totalStamps) * 100 : 0;
    }
  }

  valueObtained(card: any): string {
    const stampCard =
      card?.card && typeof card.card !== 'string'
        ? (card.card?.[0] ?? card.card)
        : card.card;

    if (stampCard?.cardType === CardType.point) {
      const totalPoints = stampCard?.points?.totalPoints ?? 0;
      const currentPoints = card?.numberOfPoints ?? 0;

      return `${
        currentPoints > totalPoints ? totalPoints : currentPoints
      }/${totalPoints}`;
    } else if (stampCard?.cardType === CardType.openEndedPoint) {
      const maxPoints = stampCard?.points?.maxPoints ?? 0;
      const currentPoints = card?.numberOfPoints ?? 0;

      if (maxPoints > 0) {
        return `${currentPoints}/${maxPoints}`;
      } else {
        return `${currentPoints}`;
      }
    } else {
      const totalStamps = stampCard?.numberOfStamps ?? 0;
      const currentStamps = card?.numberOfStamps ?? 0;

      return `${
        currentStamps > totalStamps ? totalStamps : currentStamps
      }/${totalStamps}`;
    }
  }

  isLocationNotString(events: any[]): boolean {
    return events?.length > 0 && typeof events[0]?.location !== 'string';
  }

  toggleDisabled(email: boolean): boolean {
    if (!this.merchantActive) {
      return true;
    }

    if (typeof this.customer.user !== 'string') {
      if (
        this.customer.user?.[
          email ? 'emailNotificationsOff' : 'notificationsOff'
        ]?.includes((this.authService.merchantObj?._id ?? '').toString())
      ) {
        return true;
      } else {
        return false;
      }
    }

    return false;
  }

  setNotificationEnabledSettings() {
    const merchantId = this.authService.merchantObj?._id ?? '';

    if (typeof this.customer.user !== 'string') {
      if (
        this.customer.user?.['emailNotificationsOff']?.includes(
          merchantId.toString(),
        )
      ) {
        this.emailNotificationsEnabled = false;
      } else {
        this.emailNotificationsEnabled = true;
      }

      if (
        this.customer.user?.['notificationsOff']?.includes(
          merchantId.toString(),
        )
      ) {
        this.pushNotificationsEnabled = false;
      } else {
        this.pushNotificationsEnabled = true;
      }
    }
  }

  toggleCustomerNotifications(email: boolean): void {
    if (email) {
      if (!this.emailNotificationsEnabled) {
        this.process(email);
      } else {
        return;
      }
    } else {
      if (!this.pushNotificationsEnabled) {
        this.process(email);
      } else {
        return;
      }
    }
  }

  process(email: boolean): void {
    const dialogRef = this.dialog.open(TwoOptionAlertComponent, {
      data: {
        title: 'Notifications',
        body: `Notification preferences can only be re-enabled by the user. Are you sure you want to proceed with turning off this notification preference?`,
        buttonOne: 'Cancel',
        buttonTwo: 'Yes',
      },
      scrollStrategy: new NoopScrollStrategy(),
      autoFocus: false,
      width: '350px',
      disableClose: true,
      panelClass: 'custom-dialog',
    });
    dialogRef.afterClosed().subscribe((option: number) => {
      if (option == 1) {
        this.saving = true;
        this.merchantService
          .toggleCustomerNotifications({
            customer: this.userId,
            email: email,
          })
          .subscribe({
            next: (res: any) => {
              if (
                typeof this.customer.user !== 'string' &&
                this.authService.merchantObj?._id
              ) {
                if (this.customer?.user) {
                  this.customer.user!.email = undefined;
                }

                const notificationsKey = email
                  ? 'emailNotificationsOff'
                  : 'notificationsOff';

                const merchantId = this.authService.merchantObj?._id;

                if (
                  !this.customer.user?.[notificationsKey]?.includes(merchantId)
                ) {
                  this.customer.user?.[notificationsKey]?.push(merchantId);
                }
              }
            },
            error: (error: HttpErrorResponse) => {
              this.constants.snack(error?.error?.message);
            },
          })
          .add(() => (this.saving = false));
      } else {
        if (email) {
          setTimeout(() => {
            this.emailNotificationsEnabled = true;
          });
        } else {
          setTimeout(() => {
            this.pushNotificationsEnabled = true;
          });
        }
      }
    });
  }

  cardType(card: any): string {
    const stampCard =
      card?.card && typeof card.card !== 'string'
        ? (card.card?.[0] ?? card.card)
        : card.card;

    return stampCard.cardType === CardType.stamp
      ? 'Stamp'
      : stampCard.cardType === CardType.point
        ? 'Point'
        : 'Point (Open Ended)';
  }
}
